import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { message, Button, Modal, Typography, Form } from 'antd';
import { useRefetch } from '../EntityContext';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { useMoveDomainMutation } from '../../../../graphql/domain.generated';
import DomainParentSelect from './DomainParentSelect';
import { useHandleMoveDomainComplete } from './useHandleMoveDomainComplete';
import { useDomainsContext } from '../../../domain/DomainsContext';
import { useTranslation } from '../../../../i18n/useTranslation';
import { EntityType } from '../../../../types.generated';

const StyledItem = styled(Form.Item)`
    margin-bottom: 0;
`;

const OptionalWrapper = styled.span`
    font-weight: normal;
`;

interface Props {
    onClose: () => void;
}

function MoveDomainModal(props: Props) {
    const { onClose } = props;
    const { t } = useTranslation();
    const { entityData } = useDomainsContext();
    const domainUrn = entityData?.urn;
    const [form] = Form.useForm();
    const entityRegistry = useEntityRegistry();
    const [selectedParentUrn, setSelectedParentUrn] = useState('');
    const refetch = useRefetch();

    const [moveDomainMutation] = useMoveDomainMutation();

    const { handleMoveDomainComplete } = useHandleMoveDomainComplete();

    function moveDomain() {
        if (!domainUrn) return;

        moveDomainMutation({
            variables: {
                input: {
                    resourceUrn: domainUrn,
                    parentDomain: selectedParentUrn || undefined,
                },
            },
        })
            .then(() => {
                // Only close on success so a failure keeps the modal open for the user to retry.
                onClose();
                message.loading({ content: t('Updating...'), duration: 2 });
                const newParentToUpdate = selectedParentUrn || undefined;
                handleMoveDomainComplete(domainUrn, newParentToUpdate);
                setTimeout(() => {
                    message.success({
                        content: t('Moved {{displayName}}!', {
                            displayName: t(entityRegistry.getEntityName(EntityType.Domain) || ''),
                        }),
                        duration: 2,
                    });
                    refetch();
                }, 2000);
            })
            .catch((e) => {
                message.destroy();
                message.error({ content: t('Failed to move: {{message}}', { message: e.message || '' }), duration: 3 });
            });
    }

    return (
        <Modal
            title={t('Move')}
            data-testid="move-domain-modal"
            visible
            className="f-modal"
            centered
            onCancel={onClose}
            footer={
                <>
                    <Button onClick={onClose} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button onClick={moveDomain} data-testid="move-domain-modal-move-button" type="primary">
                        {t('Move')}
                    </Button>
                </>
            }
        >
            <Form form={form} initialValues={{}} layout="vertical">
                <Form.Item
                    label={
                        <Typography.Text strong>
                            {t('Move To')} <OptionalWrapper>({t('Optional')})</OptionalWrapper>
                        </Typography.Text>
                    }
                >
                    <StyledItem name="parent">
                        <DomainParentSelect
                            selectedParentUrn={selectedParentUrn}
                            setSelectedParentUrn={setSelectedParentUrn}
                            isMoving
                        />
                    </StyledItem>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default MoveDomainModal;
